import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ListItem, ListItemButton, ListItemIcon, Typography, useTheme } from '@mui/material';
import { AWSuggestionContent } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

interface Props {
  suggestion: AWSuggestionContent;
  position: [number, number];
  /** 'default' keeps the legacy list row, 'panel' renders the two-zone panel design */
  variant?: 'default' | 'panel';

  insertAtPosition(word: string, pos: number | number[]): () => void;
}

const TextSuggestion: React.FC<Props> = ({ suggestion, position, variant = 'default', insertAtPosition }) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();

  const highlightTextSpan = (text: string, highlights?: number[][]) => {
    const splitArray: any[] = [];
    highlights?.forEach((highlight, index) => {
      if (index !== 0) {
        const textBetweenHighlights = text.substring(highlights[index - 1][1], highlights[index][0]);
        splitArray.push({ text: textBetweenHighlights, highlight: false });
      } else {
        const textBetweenHighlights = text.substring(0, highlight[0]);
        splitArray.push({ text: textBetweenHighlights, highlight: false });
      }

      const currHighlight = text.substring(highlight[0], highlight[1]);
      splitArray.push({ text: currHighlight, highlight: true });

      if (index === highlights.length - 1) {
        const rest = text.substring(highlight[1], text.length);
        splitArray.push({ text: rest, highlight: false });
      }
    });

    return splitArray.length === 0
      ? text
      : splitArray.map((split, index) => (
          <span key={index} style={{ color: split.highlight ? theme.palette.primary.main : undefined }}>
            {split.text}
          </span>
        ));
  };

  return (
    <ListItem className={classes.listItem}>
      <ListItemButton className={classes.listItemButton} onClick={insertAtPosition(suggestion.content, position)}>
        <ListItemIcon className={classes.listItemIcon}>
          <ArrowForwardIcon />
        </ListItemIcon>
        <Typography className={cx(classes.listItemText, variant === 'panel' ? classes.listItemTextPanel : null)}>
          {_.isNil(suggestion.tooltipHighlights) ? suggestion.content : highlightTextSpan(suggestion.content, suggestion.tooltipHighlights)}
          {!_.isNil(suggestion.score_improvement) ? <span className={classes.score}>+{suggestion.score_improvement} Match</span> : null}
        </Typography>
      </ListItemButton>
    </ListItem>
  );
};

const useStyles = makeStyles()((theme) => ({
  listItem: {
    padding: 0,
    position: 'relative',
  },
  listItemButton: {
    padding: '4px 0',
  },
  listItemIcon: {
    minWidth: 0,
    paddingRight: '4px',
    marginBottom: 'auto',
    '& svg': {
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.primary.main,
    },
  },
  listItemText: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 'bold',
  },
  listItemTextPanel: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.5,
    fontWeight: 500,
  },
  score: {
    color: theme.palette.analysisSecondary.main,
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 'bold',
    marginLeft: '4px',
    whiteSpace: 'nowrap',
  },
}));

export default TextSuggestion;
